/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SelectTimeGroup.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Reusable component for selecting a time group with search functionality.
    Used in time conditions for selecting the time schedule.

 🔧 Notes:
    - Searchable select component
    - Single selection
    - Filters by time group name and description

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface TimeGroup {
  id: number;
  name: string;
  description?: string;
}

interface SelectTimeGroupProps {
  value: number | null;
  onChange: (value: number | null) => void;
  timeGroups: TimeGroup[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

export default function SelectTimeGroup({
  value,
  onChange,
  timeGroups,
  placeholder = 'Select Time Group',
  disabled = false,
  className = '',
  error = false
}: SelectTimeGroupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get selected time group
  const selectedTimeGroup = value ? timeGroups.find(g => g.id === value) : null;

  // Filter time groups based on search
  const filteredTimeGroups = timeGroups.filter(group =>
    group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (group.description && group.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (groupId: number | null) => {
    onChange(groupId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = () => {
    onChange(null);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-md
          bg-background text-foreground
          ${error ? 'border-red-500' : 'border-input'}
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between
          transition-colors
        `}
      >
        <span className={selectedTimeGroup ? '' : 'text-muted-foreground'}>
          {selectedTimeGroup ? selectedTimeGroup.name : placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search time groups..."
              className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {/* Clear option (show only if a value is selected) */}
            {value !== null && (
              <button
                type="button"
                onClick={handleClear}
                className="w-full px-3 py-2 text-left rounded-md hover:bg-accent hover:text-accent-foreground flex items-center justify-between transition-colors text-muted-foreground"
              >
                <span className="text-sm italic">Clear selection</span>
              </button>
            )}
            {filteredTimeGroups.length > 0 ? (
              filteredTimeGroups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => handleSelect(group.id)}
                  className={`
                    w-full px-3 py-2 text-left rounded-md
                    hover:bg-accent hover:text-accent-foreground
                    flex items-center justify-between
                    transition-colors
                    ${value === group.id ? 'bg-accent text-accent-foreground' : ''}
                  `}
                >
                  <div className="flex-1">
                    <div className="font-medium">{group.name}</div>
                    {group.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {group.description}
                      </div>
                    )}
                  </div>
                  {value === group.id && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No time groups found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

